// Belirteç deposu. Erişim belirteci (15 dk) ve CSRF belirteci yalnız sekme belleğinde tutulur. Yenileme belirteci (7 gün)
// localStorage'da tutulur ve tüm sekmeler tarafından paylaşılır: yeni sekmede yeniden giriş istenmez, bir sekmede çıkış
// tüm sekmeleri kapatır. Yenileme belirteci her kullanımda döndüğü için yenileme sekmeler arası kilitle yapılır
// (api/client.ts). Control Plane belirteçleri yanıt gövdesinde döndürdüğü için httpOnly çerez kullanılamıyor; XSS
// riskine karşı sıkı CSP uygulanır.
// "Bu cihazı hatırla" belirteçleri (DEVICE_KEY) e-posta başına saklanır; çıkışta silinmez, böylece aynı cihazdan
// girişte OTP istenmez. Sunucu şifre değişimi, pasifleştirme ve yetki değişiminde bunları geçersiz kılar.

const REFRESH_KEY = 'cp.rt'
const DEVICE_KEY = 'cp.devices'

let accessToken: string | null = null
let csrfToken: string | null = null
let userId: string | null = null

const accessListeners = new Set<(access: string | null) => void>()

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

// Eski sürüm yenileme belirtecini sekmeye özel sessionStorage'da tutuyordu: açık oturum bir kez taşınır.
try {
  const legacy = window.sessionStorage.getItem(REFRESH_KEY)
  if (legacy) {
    if (!storage()?.getItem(REFRESH_KEY)) storage()?.setItem(REFRESH_KEY, legacy)
    window.sessionStorage.removeItem(REFRESH_KEY)
  }
} catch {
  /* depolama kapalı */
}

function readDevices(): Record<string, string> {
  try {
    const raw = storage()?.getItem(DEVICE_KEY)
    const v: unknown = raw ? JSON.parse(raw) : {}
    return v && typeof v === 'object' ? (v as Record<string, string>) : {}
  } catch {
    return {}
  }
}

const deviceKey = (email: string) => email.trim().toLocaleLowerCase('tr')

function notifyAccess() {
  accessListeners.forEach((fn) => fn(accessToken))
}

export const tokens = {
  get access() {
    return accessToken
  },
  get csrf() {
    return csrfToken
  },
  get refresh(): string | null {
    return storage()?.getItem(REFRESH_KEY) ?? null
  },
  get userId() {
    return userId
  },
  setUserId(id: string | null) {
    userId = id
  },
  set(pair: { access_token: string; refresh_token: string; csrf_token?: string }) {
    accessToken = pair.access_token
    if (pair.csrf_token) csrfToken = pair.csrf_token
    storage()?.setItem(REFRESH_KEY, pair.refresh_token)
    notifyAccess()
  },
  setCsrf(token: string) {
    csrfToken = token
  },
  clear() {
    accessToken = null
    csrfToken = null
    userId = null
    storage()?.removeItem(REFRESH_KEY)
    notifyAccess()
  },
  /** "Bu cihazı hatırla" belirteci (e-posta başına). */
  deviceToken(email: string): string | undefined {
    return readDevices()[deviceKey(email)]
  },
  setDeviceToken(email: string, token: string | null) {
    const all = readDevices()
    if (token) all[deviceKey(email)] = token
    else delete all[deviceKey(email)]
    try {
      storage()?.setItem(DEVICE_KEY, JSON.stringify(all))
    } catch {
      /* depolama kapalı */
    }
  },
  /**
   * Başka sekmede oturum açılınca ('in') ya da kapanınca ('out') çağrılır. Yalnız erişim belleği temizlenir; paylaşılan
   * yenileme belirtecine dokunulmaz (o sekme zaten silmiştir).
   */
  onOtherTab(fn: (event: 'in' | 'out') => void) {
    const handler = (e: StorageEvent) => {
      if (e.key !== REFRESH_KEY) return
      if (!e.newValue) {
        accessToken = null
        csrfToken = null
        userId = null
        fn('out')
      } else if (!e.oldValue) fn('in')
    }
    window.addEventListener('storage', handler)
    return () => window.removeEventListener('storage', handler)
  },
  /** Erişim belirteci değişince (oturum açma / yenileme / çıkış) dinleyiciler bilgilendirilir — WS yeniden bağlanır. */
  onAccessChange(fn: (access: string | null) => void) {
    accessListeners.add(fn)
    return () => {
      accessListeners.delete(fn)
    }
  },
}
